import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Plural from 'components/utils/Formatters/Plural'
import Group from 'components/utils/Group'
import { SHORT_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { useAppSelector } from 'hooks/reduxHooks'
import { subDays, addYears, addDays, daysUntilDate } from 'lib/datetime/dateUtils'
import LoyaltyAccountNightsProgress from 'loyalty/components/Progress/LoyaltyAccountNightsProgress'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import moment from 'moment'
import React, { useMemo } from 'react'
import LoyaltyAccountNextTierProgressBar from './LoyaltyAccountTierProgressBar'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'

function LoyaltyAccountTierProgressContent() {
  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data!)
  const {
    numberOfNights,
    statusReviewDate,
  } = loyaltyAccount

  const [, tierName, nextTier, nextTierName] = useCurrentLoyaltyTier()

  const membershipYearRange = useMemo(() => {
    if (!statusReviewDate) return ''

    const reviewDate = new Date(statusReviewDate)
    const endDate = subDays(reviewDate, 1)

    const yearBeforeEndDate = addYears(endDate, -1)
    const startDate = addDays(yearBeforeEndDate, 1)

    const startMonthYear = moment(startDate).format(SHORT_MONTH_LONG_YEAR)
    const endMonthYear = moment(endDate).format(SHORT_MONTH_LONG_YEAR)

    return `${startMonthYear} -  ${endMonthYear}`
  }, [statusReviewDate])

  const daysLeft = useMemo(() => {
    if (!statusReviewDate) return 0

    // review date is the *last day* it's valid
    // so it becomes invalid the day after
    return daysUntilDate(new Date(statusReviewDate)) + 1
  }, [statusReviewDate])

  const [pendingPoints] = useCurrentLoyaltyPendingPoints()

  const potentialCredits = loyaltyAccount.statusCredits + pendingPoints.statusCredits
  const creditsToProgress = Math.max(loyaltyAccount.totalCreditsForTierUpgrade - potentialCredits, 0)
  const creditsToRetainRemaining = Math.max(loyaltyAccount.totalCreditsForTierRetention - potentialCredits, 0)

  const requiredTotalNights = nextTier ? loyaltyAccount.numberOfNightsForTierUpgrade : loyaltyAccount.numberOfNightsForTierRetention
  const nightsRemaining = Math.max(requiredTotalNights - loyaltyAccount.numberOfNights, 0)
  const hasRetained = creditsToRetainRemaining === 0

  return <VerticalSpacer gap={24}>
    <Heading variant="heading3">
      {!!nextTierName && <>
        {hasRetained && <>To reach <i>{nextTierName.toLowerCase()}</i></>}
        {!hasRetained && <>To retain <i>{tierName.toLowerCase()}</i></>}
      </>}
      {!nextTierName && <>To maintain <i>{tierName.toLowerCase()}</i></>}
    </Heading>
    <VerticalSpacer gap={8}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold">Status credits</BodyText>
          <Group direction="horizontal" gap={8} wrap="wrap">
            <BodyText variant="small" colour="neutral-two">
              <b><FormatDecimal value={pendingPoints.statusCredits} /></b> pending
            </BodyText>
            <BodyText variant="small" colour="neutral-two">•</BodyText>
            <BodyText variant="small" colour="neutral-two">
              {!hasRetained && <><b><FormatDecimal value={creditsToRetainRemaining} /></b> left to retain {tierName.toLowerCase()}</>}
              {hasRetained && <><b><FormatDecimal value={creditsToProgress} /></b> remaining</>}
            </BodyText>
          </Group>
        </VerticalSpacer>
        <Group direction="horizontal" verticalAlign="center" gap={4}>
          <Heading variant="heading2"><FormatDecimal value={loyaltyAccount.statusCredits} /></Heading>
          <Caption variant="small" weight="bold">STATUS<br />CREDITS</Caption>
        </Group>
      </Group>
      <LoyaltyAccountNextTierProgressBar />
    </VerticalSpacer>
    <VerticalSpacer gap={12}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold">Nights stayed</BodyText>
          <BodyText variant="small" colour="neutral-two">
            <b><FormatDecimal value={nightsRemaining} /></b> remaining
          </BodyText>
        </VerticalSpacer>
        <Group direction="horizontal" verticalAlign="center" gap={4}>
          <Heading variant="heading2"><FormatDecimal value={numberOfNights} /></Heading>
          <Caption variant="small" weight="bold" format="uppercase">
            <Plural singular="night" count={numberOfNights} /><br />STAYED
          </Caption>
        </Group>
      </Group>
      {!!nightsRemaining && <LoyaltyAccountNightsProgress
        numberOfNights={numberOfNights}
        numberOfNightsForTierUpgrade={nightsRemaining}
      />}
    </VerticalSpacer>
    {membershipYearRange && (
      <VerticalSpacer gap={4}>
        <BodyText variant="medium" weight="bold">Membership year</BodyText>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <BodyText variant="medium">{membershipYearRange}</BodyText>
          <StatusLabel variant="pending">
            <Plural singular="day" count={daysLeft} withCount/> left
          </StatusLabel>
        </Group>
      </VerticalSpacer>
    )}
  </VerticalSpacer>
}

export default LoyaltyAccountTierProgressContent
